// src/apps/meeting/AppController.ts
import { BaseAppController } from '@shared/app/BaseAppController';
import { AppService } from './AppService';
import { useAppStore } from './AppStore';
import { manifest } from './manifest';
import { Logger } from '../../shared/services/logger.service';
import { GlobalEventService } from '../../host/services/event.service';
import { MeetingService } from './services/MeetingService';
import { MeetingEventRouter } from './events/MeetingEventRouter';
import { usePanelStore } from '@/host/stores/floatingPanels.store';
import useBreakoutRoomStore from '../breakoutSession/stores/breakoutroom.store';
import { useWorkplaceStore } from '@/host/stores';
import { useMeetingStore } from '@/host/stores/meeting.store';

class AppController extends BaseAppController {
  private meetingEventRouter: MeetingEventRouter;
  private readonly handleBreakoutSessionCreated = async () => {
    const { open, closeByAppId } = usePanelStore.getState();
    const currentRoom = useWorkplaceStore.getState().currentRoom;
    const { loadSessionsFromRoomId } = useBreakoutRoomStore.getState();
    const currentUser = useWorkplaceStore.getState().user;

    await loadSessionsFromRoomId(currentRoom.pid, true);

    const currentBreakoutRoomGroups = useBreakoutRoomStore.getState().currentBreakoutRoomGroups;
    const userPid = currentUser?.pid ?? '';
    const group = currentBreakoutRoomGroups.find(
      (g) => g.mainRoomId === currentRoom.pid && g.status === 'in-progress'
    );
    const isCurrentUserInBreakout =
      !!group &&
      (group.creatorUserPid === userPid ||
        group.rooms.some((room) => room.users.includes(userPid)));

    if (isCurrentUserInBreakout) {
      closeByAppId('breakoutSession');
      open({ title: 'Breakout Session', appId: 'breakoutSession', viewName: 'activeSession' });
    }
  };
  private readonly handleBreakoutSessionEnded = async (data: any) => {
    const { open, closeByAppId } = usePanelStore.getState();
    const currentRoom = useWorkplaceStore.getState().currentRoom;
    const { loadSessionsFromRoomId, switchMainRoom } = useBreakoutRoomStore.getState();
    const currentUser = useWorkplaceStore.getState().user;

    await switchMainRoom(data.detail.data.mainRoomJid);
    await loadSessionsFromRoomId(currentRoom.pid, true);

    const refreshedBreakoutRoomGroups = useBreakoutRoomStore.getState().currentBreakoutRoomGroups;
    const userPid = currentUser?.pid ?? '';
    const group = refreshedBreakoutRoomGroups.find(
      (g) => g.mainRoomId === currentRoom.pid && g.status === 'in-progress'
    );
    const isCurrentUserInBreakout =
      !!group &&
      (group.creatorUserPid === userPid ||
        group.rooms.some((room) => room.users.includes(userPid)));

    if (isCurrentUserInBreakout) {
      closeByAppId('breakoutSession');
      open({ title: 'Breakout Session', appId: 'breakoutSession', viewName: 'activeSession' });
    } else {
      closeByAppId('breakoutSession');
    }
  };

  /**
   * The AppService contains the business logic, event handling,
   * and notification logic for this app.
   */
  public service: AppService;
  private meetingService: MeetingService;

  constructor() {
    // Initializes base controller with app manifest and state access
    super(manifest, useAppStore.getState());
    this.meetingService = MeetingService.instance;
    this.meetingEventRouter = new MeetingEventRouter(GlobalEventService, this.meetingService);
    this.meetingService.setEventRouterInstance(this.meetingEventRouter);
    // Instantiate the service but do NOT register listeners here
    this.service = new AppService();
  }

  /**
   * Called by the host platform when the application is ready.
   * This is the correct place to:
   * - Initialize your AppService (which registers event listeners)
   * - Mark the app as ready in the app store
   */
  onAppReady() {
    Logger.debug(`[${this.manifest.uuid}] Application is ready!`);
    this.meetingEventRouter.registerEventListeners();

    // Initialize auto-streaming watcher for recording-driven transcription
    this.meetingService.initializeAutoStreamingWatcher();

    // Delegate initialization to the service
    this.service.init();

    window.addEventListener('breakoutSessionCreated', this.handleBreakoutSessionCreated);
    window.addEventListener('breakoutSessionEnded', this.handleBreakoutSessionEnded);

    // Update the app store state to reflect readiness
    useAppStore.getState().setAppReady();
  }

  // ========================================
  // EXTERNAL API - Meeting Operations
  // These methods are called by external services (BreakService, etc.)
  // ========================================

  /**
   * Check if user is currently in an active meeting — delegates to
   * AppService.isInMeeting(), which reads the real meeting store state
   * (meetingRoomState === JOINED). Previously hardcoded to always return
   * false here, which silently no-opped every external caller of this
   * method (BreakService's leave-meeting-on-break logic included).
   */
  isInMeeting(): boolean {
    try {
      return this.service.isInMeeting();
    } catch (error) {
      console.warn('[AppController] Error checking meeting status:', error);
      return false;
    }
  }

  /**
   * The room pid of the meeting currently joined, or '' if none — used to
   * rejoin after a break. currentRoomId can hold three different shapes
   * (see meeting.store.ts): the full Jitsi id (…-meeting-<pid>), the
   * hung-up "meeting-<pid>" form, or just the short pid directly during a
   * normal active meeting. getCurrentRoomPid()'s own regex only matches the
   * first two, so it returns '' during the common "normal active meeting"
   * case — falling back to currentRoomId itself (when it isn't in either
   * "meeting-" form) covers that gap.
   */
  getCurrentMeetingRoomPid(): string {
    try {
      const meetingState = useMeetingStore.getState();
      const pid = meetingState.getCurrentRoomPid();
      if (pid) return pid;

      const roomId = meetingState.currentRoomId;
      if (!roomId) return '';
      const match = roomId.match(/meeting-(.+)$/);
      return match ? match[1] : roomId;
    } catch (error) {
      console.warn('[AppController] Error reading current meeting room pid:', error);
      return '';
    }
  }

  onAppDestroy() {
    this.meetingEventRouter.unregisterEventListeners();
    window.removeEventListener('breakoutSessionCreated', this.handleBreakoutSessionCreated);
    window.removeEventListener('breakoutSessionEnded', this.handleBreakoutSessionEnded);
  }

  /**
   * Leave Meeting
   */
  async leaveMeeting(): Promise<any> {
    if (this.isInMeeting()) {
      this.service.leaveMeeting();
    }
  }

  async joinMeeting(roomPid: string): Promise<any> {
    this.service.joinMeeting(roomPid);
  }
}

// Export the singleton controller used by the host app
export const AppSDK = new AppController();
